import { formatPhoneNumber } from "react-phone-number-input";
import dayjs from "dayjs";
import { DATE_FORMAT, DATE_TIME_FORMAT, MAX_IMAGE_SIZE } from "../config/constant";

const Utils = {
  debounce: (func, delay) => {
    let timeoutId;
    return function (...args) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  },
  // Star widgets render in half-star steps, so an average like 4.3 must be
  // snapped to the nearest 0.5 (4.5) before being handed to the Rate value.
  roundRateToHalf: (rate) => {
    return Math.round((Number(rate) || 0) * 2) / 2;
  },
  roundNumber: (number) => {
    return new Intl.NumberFormat(
      "en-US",
      { style: "currency", currency: "USD" },
      { minimumFractionDigits: 2, maximumFractionDigits: 2, currency: "USD" }
    )
      .format(number)
      .replace(/,/g, " ");
  },
  getMinandMaxVariants: (arr) => {
    const newarr = [...arr];
    const sortarr = newarr?.sort((a, b) => a?.price - b?.price);
    return sortarr?.length > 1
      ? [sortarr[0], sortarr[sortarr?.length - 1]]
      : [sortarr[0]];
  },
  filterOption: (input, option) =>
    (option?.label ?? "")?.toLowerCase()?.includes(input?.toLowerCase()),
  findParentCategory: (product) => {
    return product?.categories?.length
      ? product?.categories?.find(
          (el) => el?.parent === null && el?.name?.toLowerCase() !== "featured"
        )
      : "";
  },
  mapperOfMeasurment: (unit) => {
    switch (unit) {
      case "kilograms":
        return "kg";
      case "kilogram":
        return "kg";
      case "milligrams":
        return "mg";
      case "milligram":
        return "mg";
      case "grams":
        return "g";
      case "gram":
        return "g";
      case "pounds":
        return "lb";
      case "pound":
        return "lb";
      case "ounces":
        return "oz";
      case "ounce":
        return "oz";
      default:
        return "";
    }
  },
  formatWeight: (weight) => {
    // Convert the number to a string
    const numberString = weight?.toString();

    // If the number has a decimal point, remove trailing zeros after the decimal
    if (numberString?.includes(".")) {
      return parseFloat(weight);
      // fixed by two variant parseFloat(parseFloat(weight).toFixed(2));
    }

    // If there's no decimal or it's already an integer, return the number as it is
    return weight;
  },
  showProductDetail(variant) {
    if (!variant) return "";
    return this?.mapperOfMeasurment(variant?.unit)
      ? `${this?.formatWeight(
          variant?.name?.split(" ")[0]
        )} ${this?.mapperOfMeasurment(variant?.unit)}`?.replace(/\s+/g, "")
      : `${this?.formatWeight(variant?.name?.split(" ")[0])} ${
          variant?.unit
        }`?.replace(/\s+/g, "");
  },
  getMaxPrice: (arr) => {
    let maxPrice = 0;
    if (Array.isArray(arr)) {
      arr?.forEach((item) => {
        if (Array.isArray(item?.variants) && item?.variants?.length) {
          item?.variants?.forEach((variant) => {
            const price = parseFloat(variant?.price); // Convert price to number
            if (!isNaN(price) && price > maxPrice && isFinite(price)) {
              maxPrice = price;
            }
          });
        }
      });
    }

    return Number(maxPrice);
  },
  findVariantCountGt0: (variants) =>
    variants?.find((variant) => variant?.count > 0),
  // Stock label shown next to a weight when the shop setting
  // is_count_visible_on_shop is on: "12 left" / "1 left". Sold-out
  // variants get "" so the disabled button never reads "0 left".
  // Keep the wording identical to the staff app (CANA-640).
  showVariantCount: (variant) => {
    const count = Number(variant?.count);
    if (!Number.isFinite(count) || count <= 0) return "";
    return `${count} left`;
  },
  getUrlParamsAsObject: (searchParams) => {
    if (!searchParams) return {}; // Return empty object if searchParams is null or undefined
    return Object.fromEntries(searchParams?.entries());
  },
  get_filtered_url_params: (old_url_params, new_url_params) => {
    if (!old_url_params && !new_url_params) return {};
    const old_params_as_object = Utils?.getUrlParamsAsObject(old_url_params);
    const filtered_params_as_object = Object?.fromEntries(
      Object?.entries({ ...old_params_as_object, ...new_url_params })?.filter(
        ([_, value]) => Boolean(value) || value === 0
      )
    );
    return filtered_params_as_object;
  },
  madeUnique: (items) => {
    return [...new Map(items?.map((item) => [item["id"], item]))?.values()];
  },
  getUrlInMap: (address) => {
    return `http://maps.apple.com/?q=${encodeURIComponent(address)}`;
  },
  getDisplayAddress: (item) => {
    const street = item?.address1 ? item?.address1?.trim() : "";
    const parts = [item?.city, item?.state, item?.zipcode]?.filter(Boolean);
    const streetHasAllParts =
      Boolean(street) &&
      parts?.length > 0 &&
      parts?.every((part) =>
        street?.toLowerCase()?.includes(part?.trim()?.toLowerCase())
      );
    return [
      street,
      item?.address2,
      ...(streetHasAllParts ? [] : [item?.city, item?.state, item?.zipcode]),
    ]
      ?.filter(Boolean)
      ?.join(", ");
  },
  getStreetAddress: ({ address, city, state, zipcode }) => {
    if (!address) return "";
    let street = address;
    [zipcode, state, city]?.forEach((part) => {
      const suffix = `, ${part}`;
      if (part && street?.toLowerCase()?.endsWith(suffix?.toLowerCase())) {
        street = street?.slice(0, street?.length - suffix?.length);
      }
    });
    return street;
  },
  format_phone_number: (phone_number) => {
    return phone_number ? formatPhoneNumber(phone_number) : "";
  },
  format_date: (date) => {
    return date ? dayjs(date)?.format("YYYY-MM-DD") : "";
  },
  display_date: (date) => {
    return date ? dayjs(date)?.format(DATE_FORMAT) : "";
  },
  formatDateTime: (date) => {
    return date ? dayjs(date).format(DATE_TIME_FORMAT) : "";
  },
  scrollIntoViewByClassName: (className) => {
    if (
      typeof className === "string" &&
      className &&
      className?.includes(".")
    ) {
      const element = document?.querySelector(className); // Select the element
      if (element) {
        element?.scrollIntoView({ behavior: "smooth" });
      }
    }
  },
  parsePrice: (price) => {
    if (price === null || price === undefined || price === "") {
      return 0;
    }
    const parsed =
      typeof price === "string" ? parseFloat(price) : Number(price);
    return isNaN(parsed) || !isFinite(parsed) ? 0 : parsed;
  },
  base64ToFile: (base64String, fileName = "Id-Image.png") => {
    if (!base64String || !base64String.includes(",")) {
      return null;
    }
    const arr = base64String.split(",");
    const mimeMatch = arr[0]?.match(/:(.*?);/);
    if (!mimeMatch) {
      return null;
    }
    const mime = mimeMatch[1];
    const byteString = atob(arr[1]);
    let n = byteString.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = byteString.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  },
  // Safely turns ANY error-shaped value into a displayable string, without
  // assuming a specific payload structure. Whatever the backend sends —
  // string, number, array, nested object, Error instance — it recursively
  // walks every property and joins the readable text it finds.
  // `seen` guards against circular references so it can never infinite-loop.
  getErrorText: (value, seen) => {
    // Primitives that read well on their own.
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    // Functions, symbols, etc. carry no displayable message.
    if (typeof value !== "object") return "";

    // Guard against circular references before recursing into objects/arrays.
    const visited = seen || new WeakSet();
    if (visited.has(value)) return "";
    visited.add(value);

    // Native Error instances expose their text via `message`.
    if (value instanceof Error) return value.message || "";

    // Arrays and plain objects are handled the same way: recurse over every
    // entry and join whatever readable text comes back. No key names assumed.
    const entries = Array.isArray(value) ? value : Object.values(value);
    return entries
      .map((item) => Utils.getErrorText(item, visited))
      .filter(Boolean)
      .join(" ");
  },
  isSalePriceValid: (salePrice, regularPrice) => {
    const sale = Utils?.parsePrice(salePrice);
    const regular = Utils?.parsePrice(regularPrice);
    // Sale price must be greater than 0 and less than regular price
    return sale > 0 && regular > 0 && sale < regular;
  },
  compressImage: (dataUrl) => {
    return new Promise((resolve) => {
      const MAX_DIMENSION = 1920;
      const img = new window.Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        let quality = 0.85;
        let result = canvas.toDataURL("image/jpeg", quality);
        while (result.length > MAX_IMAGE_SIZE && quality > 0.3) {
          quality -= 0.1;
          result = canvas.toDataURL("image/jpeg", quality);
        }
        resolve(result);
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  },
};

export default Utils;
